import { useState, useRef, useEffect, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router";
import Dropdown from "react-bootstrap/Dropdown";
import Button from "react-bootstrap/Button";
import InputGroup from "react-bootstrap/InputGroup";
import "./index.scss";

import { Popup, GraphDesignTool, ConfirmDialog, PerformerConstraints, ConfigContainer } from "components";
import { deleteNode, initialNodeCoords } from "components/GraphDesignTool";
import { AnalysisTaskConfig, AnalysisConnectionConfig, AnalysisGroupConfig } from "components/Config";
import { Text, Number, Container, ColorPicker, Title } from "components/Form";

import { message, analysisDefinitions, createMap, addToMap, removeFromMap, analysisGroups, limsSpecs, limsForskrifter } from "state_management";
import { FiPlus, FiCircle, FiSettings } from "react-icons/fi";
import { FaArrowUp } from 'react-icons/fa';
import { Duration } from "luxon";
import SpecEditor from "components/Lims/SpecEditor";
import RequirementsSection from "components/Lims/RequirementsSection";
import ReviewSpecification from "./ReviewSpecification";
import SystemSuitability from "./SystemSuitability";
import ForskriftLink from "./ForskriftLink";
import SpecChangeControl from "./SpecChangeControl";
import store from "store"
import useNavigationBlocker from "utils/hooks/useBlocker"
import useT from "utils/hooks/useT"

// Blank System Suitability spec (PRD-005 Area 3 / SPEC-017): SST not required, no criteria set,
// the default six replicate injections. Unset criteria are simply not judged.
const EMPTY_SST_SPEC = {
  requiresSst: false,
  rsdAreaMax: null,
  rsdRtMax: null,
  tailingMin: null,
  tailingMax: null,
  plateCountMin: null,
  resolutionMin: null,
  injectionCount: 6,
  // F4 (SPEC-036): instrument type (HPLC/GC) for the SST template picker + the optional default
  // shared template the run pre-selects. Both null by default → inline criteria apply.
  instrumentType: null,
  defaultSstTemplateId: null,
};

// Configuration page for setting up the team's analyses.
export default function AnalysisConfigurationPage({ id }) {
  const { t } = useT("common")
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [activeTask, setActiveTask] = useState(null);
  const [activeConnection, setActiveConnection] = useState(null);
  const [addConfig, setAddConfig] = useState(false);
  const [analysisGroupConfigId, setAnalysisGroupConfigId] = useState(null);
  const [showPerformerConstraints, setShowPerformerConstraints] = useState(false);

  // LIMS QC specification (F-B1) — measured parameters + acceptance criteria + a calculation
  // formula, shown only when the LIMS module is on. SPEC-021 (B2): the spec is FOLDED onto the
  // analysis-definition payload — seeded from the definition's nested `spec` key and persisted
  // by INCLUDING it in the definition save (the server snapshots results from ITS spec rows, so
  // the spec must travel on this payload). Drafts cannot carry specs (the draft schema rejects
  // the keys), so the spec sections only persist on definition saves.
  const showLims = useSelector((state) => state.teamStatus.showLims);
  const [showSpecification, setShowSpecification] = useState(false);
  const [specParameters, setSpecParameters] = useState([]);
  const [specFormula, setSpecFormula] = useState("");
  // F8 (SPEC-040) — high-level consumable requirements for this analysis (type, quantity,
  // standardClass, notes). Folded onto the SAME nested `spec` payload as parameters/formula
  // (spec.requiredConsumables); drives the run-time picker filtering + the send-to-review
  // completeness gate. Seeded once with the rest of the spec; included in buildLimsSpecPayload.
  const [requiredConsumables, setRequiredConsumables] = useState([]);
  const [showRequirements, setShowRequirements] = useState(false);
  // SPEC-016: false when any calculated parameter's formula is invalid (unknown name / bad
  // syntax). Saving is blocked until every calculated formula is valid. Measured-only specs
  // (and LIMS off) keep this true.
  const [specValid, setSpecValid] = useState(true);

  // LIMS Review Specification (PRD-003 Area 1 / SPEC-007) — which competence group(s) may
  // perform the second-person review of this analysis, and how long it takes. Folded onto the
  // definition payload like the spec (nested `reviewSpec` key); lives OUTSIDE the Task Design
  // flow (not a procedure step; must NOT count toward Minimum Duration). `reviewSpecDuration` is
  // held as a Luxon Duration (the DurationInput control's value) and persisted as seconds.
  const [showReviewSpec, setShowReviewSpec] = useState(false);
  const [reviewSpecGroups, setReviewSpecGroups] = useState([]);
  const [reviewSpecDuration, setReviewSpecDuration] = useState(null);
  // SPEC-032 (F0) — whether this analysis requires the supervisor (chemist) review step. Folded
  // onto the same nested `reviewSpec` payload; default false. Foundation only (flow lands in F3).
  const [requiresSupervisorReview, setRequiresSupervisorReview] = useState(false);

  // LIMS System Suitability spec (PRD-005 Area 3 / SPEC-017) — whether this analysis definition
  // REQUIRES an SST before results can be sent to review, plus the SST acceptance criteria.
  // Folded onto the definition payload (nested `sstSpec` key), mirroring the Review Specification.
  const [showSstSpec, setShowSstSpec] = useState(false);
  const [sstSpec, setSstSpec] = useState(EMPTY_SST_SPEC);

  // SPEC-021 (B2 / SPEC-007 D8): the reason for changing an EXISTING QC spec, audited server-side
  // (`specChangeReason` on the definition payload). Required when a definition that already had
  // QC-spec content is saved with CHANGED spec content; not required on first creation.
  const [specChangeReason, setSpecChangeReason] = useState("");
  // #4073/#4074: whether the spec-change-reason popup is open (it replaces the old inline field).
  const [specReasonPrompt, setSpecReasonPrompt] = useState(false);

  // LIMS Forskrift link (PRD-003 Area 2 / SPEC-008) — which controlled method document(s) govern
  // this analysis definition. The link lives ON THE DOCUMENT (limsForskrifter.analysisDefinitionIds,
  // many-to-many), not on the definition payload (the AD endpoint rejects unknown fields → 422), so
  // this section holds the list of selected document ids and persists it via setLinksForDefinition
  // chained after the definition save. Shown only when the LIMS module is on.
  const [showForskriftLink, setShowForskriftLink] = useState(false);
  const [forskriftDocIds, setForskriftDocIds] = useState([]);
  const forskriftDocuments = useSelector(limsForskrifter.selectDocuments);

  const competenceGroups = useSelector((state) => state.competenceGroups);
  const analysisDefinition = useSelector((state) => state.analysisDefinitions[id]);
  const analysisNames = Object.values(useSelector((state) => state.analysisDefinitions)).map(
    (analysis) => analysis.name
  );
  const activeAnalysisGroupId = useSelector((state) => state.analysisGroups.activeAnalysisGroupId);
  const [isDraft, setIsDraft] = useState(true);

  const [tasks, setTasks] = useState(analysisDefinition ? createMap(analysisDefinition.taskDefinitions) : {});
  const [connections, setConnections] = useState(
    analysisDefinition ? createMap(analysisDefinition.taskDefinitionConnections) : {}
  );
  const [performerConstraints, setPerformerConstraints] = useState(
    analysisDefinition
      ? analysisDefinition.performerConstraints.map((constraint) => ({
          firstDefinitionId: constraint.firstDefinitionId,
          secondDefinitionId: constraint.secondDefinitionId,
          performerConstraint: constraint.performerConstraint,
        }))
      : []
  );


  const [color, setColor] = useState(analysisDefinition ? analysisDefinition.color : null);
  const [name, setName] = useState(analysisDefinition ? analysisDefinition.name : "");
  const [maxSampleCount, setMaxSampleCount] = useState(analysisDefinition ? analysisDefinition.maxSampleCount : null);
  const [minSampleCount, setMinSampleCount] = useState(analysisDefinition ? analysisDefinition.minSampleCount : 0);
  const [shouldCheckChanges, setShouldCheckChanges] = useState(true);
  const [leavePageObject, setLeavePageObject] = useState({ msg: "", location: "", display: false });
  const analysisGroupsDropdown = useSelector((state) => state.analysisGroups ? state.analysisGroups.groups : []);
  const [mode, setMode] = useState("node");
  const [nodeCoords, setNodeCoords] = useState(() => initialNodeCoords(tasks, connections));

  const minTime = useMemo(() => {
    let perSampleDurationSum = 0;
    let setupTimeSum = 0;
    let connectionTimeSum = 0;

    Object.values(tasks).forEach(task => {
      perSampleDurationSum += task.perSampleDuration || 0;
      setupTimeSum += task.setuptime || 0;
    });

    Object.values(connections).forEach(connection => {
      connectionTimeSum += connection.timeBetween.min || 0;
    });

    return perSampleDurationSum + setupTimeSum + connectionTimeSum;
  }, [tasks, connections]);

  const formatTime = (seconds) => {
    const days = Math.floor(seconds / 86400);
    const hours = Math.floor((seconds % 86400) / 3600);
    const minutes = Math.floor(((seconds % 86400) % 3600) / 60);
    const formattedTime = `${days}d ${hours}h ${minutes}m`;
    return formattedTime;
  }

  //Decides whether or not the user has changed any fields on the page
  function somethingHasChanged(a) {
    var msg = ""

    if (!a) {
      msg = name ? msg.concat("- " + t("analysisName") + " ") : msg
      msg = color ? msg.concat("- " + t("color") + " ") : msg
      msg = maxSampleCount ? msg.concat("- " + t("maxSampleCount") + " ") : msg
      msg = minSampleCount ? msg.concat("- " + t("minSampleCount") + " ") : msg
      msg = performerConstraints.length ? msg.concat("- " + t("performerConstraintsCount") + " ") : msg
      msg = Object.values(tasks).length ? msg.concat("- " + t("taskCount") + " ") : msg
      msg = Object.values(connections).length ? msg.concat("- " + t("connectionCount") + " ") : msg
      return msg
    }
    //Analysis basic checks
    msg = a.color !== color ? msg.concat("- " + t("color") + " ") : msg
    msg = a.maxSampleCount !== maxSampleCount ? msg.concat("- " + t("maxSampleCount") + " ") : msg
    msg = a.minSampleCount !== minSampleCount ? msg.concat("- " + t("minSampleCount") + " ") : msg
    msg = a.name !== name ? msg.concat("- " + t("analysisName") + " ") : msg

    //Task Connection checks
    var connectionsA = a.taskDefinitionConnections
    var connectionsB = Object.values(connections)
    var Connections = false

    if (connectionsA.length === connectionsB.length) {
      for (let i = 0; i < connectionsA.length; i++) {
        Connections = connectionsA[i].id !== connectionsB[i].id ? true : Connections
        Connections = connectionsA[i].isWorkdayConnection !== connectionsB[i].isWorkdayConnection ? true : Connections
        Connections = connectionsA[i].nextId !== connectionsB[i].nextId ? true : Connections
        Connections = connectionsA[i].prevId !== connectionsB[i].prevId ? true : Connections
        Connections = connectionsA[i].samplePercentage !== connectionsB[i].samplePercentage ? true : Connections
        Connections = connectionsA[i].timeBetween.max !== connectionsB[i].timeBetween.max ? true : Connections
        Connections = connectionsA[i].timeBetween.min !== connectionsB[i].timeBetween.min ? true : Connections
      }
    } else {
      msg = msg.concat("- " + t("connectionCount"))
    }
    msg = Connections ? msg.concat("- " + t("connections")) : msg

    //Performer Constraint checks
    var constra = false
    if (a.performerConstraints.length === performerConstraints.length) {
      for (let i = 0; i < a.performerConstraints.length; i++) {
        constra =
          a.performerConstraints[i].firstDefinitionId !== performerConstraints[i].firstDefinitionId ? true : constra
        constra =
          a.performerConstraints[i].performerConstraint !== performerConstraints[i].performerConstraint ? true : constra
        constra =
          a.performerConstraints[i].secondDefinitionId !== performerConstraints[i].secondDefinitionId ? true : constra
      }
    } else {
      msg = msg.concat("- " + t("performerConstraintsCount"))
    }
    msg = constra ? msg.concat("- " + t("performerConstraintsLabel") + " ") : msg

    //Task definition checks
    let tasksA = a.taskDefinitions || [];
    let tasksB = Object.values(tasks || {});

    if (tasksA.length === tasksB.length) {
        tasksA.forEach((task, i) => {
            // Check for setup time and per sample duration at task level
            let setupTimeChanged = task.setuptime !== tasksB[i].setuptime;
            let sampleDurationChanged = task.perSampleDuration !== tasksB[i].perSampleDuration;

            // Check asset requirements within each task
            const assetReqA = task.assetRequirements || [];
            const assetReqB = tasksB[i].assetRequirements || [];
            const addedOrRemovedAsset = assetReqA.length !== assetReqB.length;

            const assetRequirementsChanged = assetReqA.length === assetReqB.length && assetReqA.some((assetReq, j) => {
                return [
                    assetReq.allowedAssetIds.length !== assetReqB[j].allowedAssetIds.length ||
                    assetReq.allowedAssetIds.some((id, k) => id !== assetReqB[j].allowedAssetIds[k]),
                    assetReq.assetId !== assetReqB[j].assetId,
                    assetReq.assetOffset !== assetReqB[j].assetOffset,
                    assetReq.assetPerSampleDuration !== assetReqB[j].assetPerSampleDuration,
                    assetReq.assetSetuptime !== assetReqB[j].assetSetuptime
                ].some(Boolean);
            });

            if (assetRequirementsChanged) {
                msg += "- " + t("assetRequirementsForAsset", { index: i + 1 }) + " ";
            }
            if (setupTimeChanged) {
                msg += "- " + t("setupTimeForPeopleSetup");
            }
            if (sampleDurationChanged) {
                msg += "- " + t("perSampleDurationForPeopleSetup");
            }
            if (addedOrRemovedAsset) {
                msg += "- " + t("assetAddedOrRemoved");
            }
        });
    } else {
        msg += "- " + t("taskCount") + " ";
    }

    return msg
  }

  function FEcheckconstraintMessage(res,isDraft, isNew) {
    if (isNew && analysisNames.includes(res.name)) {
      return t("analysisNameMustBeUnique")
    } else if(!res.name) {
      return t("pleaseFillOutAnalysisName")
    } else if(!res.color) {
      return t("pleaseChooseColor")
    } else if(res.minSampleCount !== null && res.maxSampleCount !== null && res.minSampleCount > res.maxSampleCount) {
      return t("minSampleMustBeLowerThanMax")
    } else if(!isDraft && res.taskDefinitions.length === 0) {
      return t("analysisMustContainTaskDefinition")
    }
    // when converting a draft to definition, or 'Save As' in definition
    if (!(isDraft || isNew) || (isNew && !isDraft)) {
      const taskFound = res.taskDefinitions.some(task => !task.name || task.allowedGroupIds.length === 0 || typeof(task.setuptime) !== 'number')
      if(taskFound) {
        return t("pleaseFillOutRequiredTaskParams")
      }
      return ""
   }
  }

  // SPEC-021 (B2): seed the three QC-spec editors ONCE per definition from the definition's own
  // nested payload keys (`spec` / `reviewSpec` / `sstSpec` — already loaded with the definition;
  // no separate fetch exists any more). The seed is guarded by a ref so a background definitions
  // refetch never clobbers in-progress edits. Also captures the initial-spec snapshot that drives
  // the change detection for the required specChangeReason.
  const limsSpecSeededFor = useRef(null)
  const initialLimsSpecRef = useRef(null)
  const hadLimsSpecRef = useRef(false)
  useEffect(() => {
    if (!showLims || !id || !analysisDefinition || limsSpecSeededFor.current === id) return
    limsSpecSeededFor.current = id
    const spec = analysisDefinition.spec
    const reviewSpec = analysisDefinition.reviewSpec
    const sst = analysisDefinition.sstSpec
    if (spec) {
      setSpecParameters(limsSpecs.normalizeParameters(spec.parameters))
      setSpecFormula(spec.formula || "")
      // F8 (SPEC-040): seed the consumable requirements from the same nested spec payload.
      setRequiredConsumables(limsSpecs.normalizeRequiredConsumables(spec.requiredConsumables))
    }
    if (reviewSpec) {
      setReviewSpecGroups(reviewSpec.allowedGroupIds || [])
      setReviewSpecDuration(reviewSpec.duration ? Duration.fromObject({ seconds: reviewSpec.duration }) : null)
      setRequiresSupervisorReview(!!reviewSpec.requiresSupervisorReview)
    }
    if (sst) {
      setSstSpec({ ...EMPTY_SST_SPEC, ...sst })
    }
    hadLimsSpecRef.current = !!(spec || reviewSpec || sst)
    initialLimsSpecRef.current = JSON.stringify(
      buildLimsSpecPayload({ spec, reviewSpec, sst })
    )
  }, [showLims, id, analysisDefinition])

  // The folded QC-spec keys for the definition payload (SPEC-021 B2). `seed` lets the seeding
  // effect build the snapshot from the persisted values; without it the current editor state is
  // used. The shapes match the backend schema exactly: spec {parameters, formula}, reviewSpec
  // {allowedGroupIds, duration(seconds)}, sstSpec {requiresSst, ...criteria, injectionCount}.
  function buildLimsSpecPayload(seed = null) {
    if (seed) {
      const sstSeed = seed.sst ? { ...EMPTY_SST_SPEC, ...seed.sst } : EMPTY_SST_SPEC
      return {
        spec: {
          parameters: limsSpecs.normalizeParameters(seed.spec ? seed.spec.parameters : []),
          formula: (seed.spec && seed.spec.formula) || "",
          // F8 (SPEC-040): the consumable requirements ride on the nested spec payload.
          requiredConsumables: limsSpecs.normalizeRequiredConsumables(seed.spec ? seed.spec.requiredConsumables : []),
        },
        reviewSpec: {
          allowedGroupIds: (seed.reviewSpec && seed.reviewSpec.allowedGroupIds) || [],
          duration: (seed.reviewSpec && seed.reviewSpec.duration) || null,
          requiresSupervisorReview: !!(seed.reviewSpec && seed.reviewSpec.requiresSupervisorReview),
        },
        sstSpec: sstSeed,
      }
    }
    return {
      spec: {
        parameters: limsSpecs.normalizeParameters(specParameters),
        formula: specFormula || "",
        // F8 (SPEC-040): persist the edited consumable requirements on the nested spec payload.
        requiredConsumables: limsSpecs.normalizeRequiredConsumables(requiredConsumables),
      },
      reviewSpec: {
        allowedGroupIds: reviewSpecGroups,
        duration: reviewSpecDuration ? reviewSpecDuration.as("seconds") : null,
        requiresSupervisorReview,
      },
      sstSpec: { ...EMPTY_SST_SPEC, ...sstSpec },
    }
  }

  // Whether the QC-spec content differs from what was loaded with the definition.
  const limsSpecChanged = () =>
    initialLimsSpecRef.current !== null && JSON.stringify(buildLimsSpecPayload()) !== initialLimsSpecRef.current

  // The LIMS keys merged into a DEFINITION save payload (never a draft — the draft schema rejects
  // them). Carries `specChangeReason` only when stated; the backend audits it per changed spec.
  const limsDefinitionPayload = (reasonForChange = specChangeReason) => {
    if (!showLims) return {}
    const reason = (reasonForChange || "").trim()
    return {
      ...buildLimsSpecPayload(),
      ...(reason ? { specChangeReason: reason } : {}),
    }
  }

  // Load this definition's forskrift links once the LIMS module is on: fetch the documents and
  // seed the picker from which documents already link this definition id. Keyed by the definition
  // id; only meaningful for an already-saved definition (a new/draft has no id yet).
  useEffect(() => {
    if (showLims && id) {
      dispatch(limsForskrifter.fetch()).then(() => {
        const docs = limsForskrifter.selectDocuments(store.getState())
        setForskriftDocIds(docs.filter((d) => (d.analysisDefinitionIds || []).includes(id)).map((d) => d.id))
      })
    }
  }, [showLims, id, dispatch])

  // Persist the forskrift links for a saved definition: link this definition onto the selected
  // documents and unlink it from any deselected ones. Dispatched SEPARATELY from the
  // analysis-definition save (the AD endpoint rejects unknown fields). No-op when LIMS is off.
  // Returns a promise so callers chain.
  const persistForskriftLink = (definitionId) => {
    if (!showLims || !definitionId) {
      return Promise.resolve()
    }
    return dispatch(limsForskrifter.setLinksForDefinition(definitionId, forskriftDocIds))
  }

  const isMountedRef = useRef(true)
  const shouldCheckChangesRef = useRef(true)

  useEffect(() => {
    shouldCheckChangesRef.current = shouldCheckChanges
  }, [shouldCheckChanges])

  useEffect(() => {
    return () => {
      isMountedRef.current = false
      shouldCheckChangesRef.current = false
    }
  }, [])

  const blocker = useNavigationBlocker(
    isMountedRef.current &&
    shouldCheckChangesRef.current &&
      !leavePageObject.display &&
      somethingHasChanged(analysisDefinition));
  if (blocker.state === "blocked" && !leavePageObject.display) {
    setLeavePageObject({
      msg: somethingHasChanged(analysisDefinition),
      location: blocker.tx?.nextLocation?.pathname || "",
      display: true,
    })
  }

  // SPEC-016 save guard: block saving while a calculated-parameter formula is invalid (it could
  // never compute). No-op when LIMS is off or the spec section was never opened/edited.
  const blockedByInvalidSpec = () => {
    if (showLims && !specValid) {
      dispatch(message.warning(t("limsCalcFormulaInvalidSave")))
      return true
    }
    return false
  }

  // SPEC-021 (SPEC-007 D8) save guard: changing the QC-spec content of a definition that ALREADY
  // had a spec requires a stated reason (audited server-side as specChangeReason). First-time
  // creation needs none. No-op for drafts/new definitions and when nothing spec-related changed.
  // A pure predicate now: it used to warn and force the PARAMETER section open to reveal the
  // inline reason field, which is what made a mere SST edit pop a "reason for spec change" box
  // under the parameter specification (#4073/#4074). The caller opens the popup instead.
  const needsSpecReason = () => Boolean(showLims && id && hadLimsSpecRef.current && limsSpecChanged())

  // #4073/#4074: the definition save, lifted out of the JSX so the spec-change reason can be
  // collected in a popup and handed straight in. Passing the reason as an argument (rather
  // than reading the state the popup just set) avoids acting on a stale value — a React state
  // update is not visible to the same tick that triggered the save.
  const saveDefinition = (reasonForChange) => {
        setName(name.trim())
        shouldCheckChangesRef.current = false
        setShouldCheckChanges(false)
        const trimmedName = name.trim()
        const res = {
          name: trimmedName,
          color,
          maxSampleCount,
          minSampleCount,
          performerConstraints,
          analysisGroupIds: activeAnalysisGroupId ? [activeAnalysisGroupId] : [],
          // SPEC-021 (B2): the QC specs travel ON the definition payload (definitions only).
          ...limsDefinitionPayload(reasonForChange),
          taskDefinitions: Object.values(tasks).map((task) => ({
            coordinates: nodeCoords[task.id],
            name: task.name,
            tempId: String(task.id),
            allowedGroupIds: task.allowedGroupIds ? task.allowedGroupIds.filter((groupId) => competenceGroups[groupId]) : [],
            assetRequirements: task.assetRequirements ? task.assetRequirements  : [],
            setuptime: task.setuptime,
            perSampleDuration: task.perSampleDuration,
            description: task.description,
          })),
          taskDefinitionConnections: Object.values(connections).map((connection) => ({
            tempNextId: String(connection.nextId),
            tempPrevId: String(connection.prevId),
            samplePercentage: connection.samplePercentage,
            timeBetween: connection.timeBetween,
            isWorkdayConnection: connection.isWorkdayConnection,
          })),
        }
        if(FEcheckconstraintMessage(res,false,false)) {
          return dispatch(message.warning(FEcheckconstraintMessage(res,false,false)))
        }
        if (!id) {
          dispatch(analysisDefinitions.create(res))
            .then((created) => persistForskriftLink(created && created.id))
            .then(() => navigate("/setup/analyses"))
        } else {
          // Ugly quick fix to prevent duplication
          return dispatch(analysisDefinitions.edit(res, id))
            .then((edited) => persistForskriftLink((edited && edited.id) || id))
            .then(() => navigate("/setup/analyses"))
        }
  }
  return (
    <>
    <ConfigContainer
      title={ analysisDefinition ? (analysisDefinition.isDraft ? t("analysisDraft") : t("analysisDefinition") ) : t("analysisDraft")}
      cancelHandler={() => {
          shouldCheckChangesRef.current = false
          setShouldCheckChanges(false)
          navigate("/setup/analyses")
        }}
      submitHandler={() => {
        if (blockedByInvalidSpec()) return
        // #4073/#4074: a changed spec on an existing definition needs a stated reason. Ask for it
        // in a popup at the moment of saving, instead of an inline field wedged under the
        // PARAMETER editor — which was bewildering when what you had actually edited was the SST
        // or the review spec (limsSpecChanged() covers all three).
        if (needsSpecReason()) {
          setSpecChangeReason("")
          setSpecReasonPrompt(true)
          return
        }
        saveDefinition()
      }}
      saveAsHandler={() => {
        if (blockedByInvalidSpec()) return
        setIsDraft(false)
        setName(name.trim())
        const trimmedName = name.trim()
        setShouldCheckChanges(false)
        const res = {
          name: trimmedName,
          color,
          maxSampleCount,
          minSampleCount,
          performerConstraints,
          analysisGroupIds: activeAnalysisGroupId ? [activeAnalysisGroupId] : [],
          // SPEC-021 (B2): "Save As" creates a NEW definition carrying the edited QC specs
          // (first creation for the copy — no change reason required).
          ...limsDefinitionPayload(),
          taskDefinitions: Object.values(tasks).map((task) => ({
            name: task.name,
            tempId: String(task.id),
            allowedGroupIds: task.allowedGroupIds ? task.allowedGroupIds.filter((groupId) => competenceGroups[groupId]) : [],
            assetRequirements: task.assetRequirements ? task.assetRequirements  : [],
            setuptime: task.setuptime,
            perSampleDuration: task.perSampleDuration,
            description: task.description,
          })),
          taskDefinitionConnections: Object.values(connections).map((connection) => ({
            tempNextId: String(connection.nextId),
            tempPrevId: String(connection.prevId),
            samplePercentage: connection.samplePercentage,
            timeBetween: connection.timeBetween,
            isWorkdayConnection: connection.isWorkdayConnection,
          })),
        }
        if(FEcheckconstraintMessage(res,isDraft,true)) {
          return dispatch(message.warning(FEcheckconstraintMessage(res,isDraft,true)))
        }
        dispatch(analysisDefinitions.create(res))
          .then((created) => persistForskriftLink(created && created.id))
          .then(() => navigate("/setup/analyses"))
      }}
      saveDraftHandler={() => {
        if (blockedByInvalidSpec()) return
        setIsDraft(true)
        setName(name.trim())
        shouldCheckChangesRef.current = false
        setShouldCheckChanges(false)
        const trimmedName = name.trim()
        const res = {
          name: trimmedName,
          color,
          maxSampleCount,
          minSampleCount,
          performerConstraints,
          analysisGroupIds: activeAnalysisGroupId ? [activeAnalysisGroupId] : [],
          taskDefinitions: Object.values(tasks).map((task) => ({
            coordinates: nodeCoords[task.id],
            name: task.name,
            tempId: String(task.id),
            allowedGroupIds: task.allowedGroupIds ? task.allowedGroupIds.filter((groupId) => competenceGroups[groupId]) : [],
            assetRequirements: task.assetRequirements ? task.assetRequirements  : [],
            setuptime: task.setuptime ? task.setuptime : 0,
            perSampleDuration: task.perSampleDuration,
            description: task.description,
          })),
          taskDefinitionConnections: Object.values(connections).map((connection) => ({
            tempNextId: String(connection.nextId),
            tempPrevId: String(connection.prevId),
            samplePercentage: connection.samplePercentage,
            timeBetween: connection.timeBetween,
            isWorkdayConnection: connection.isWorkdayConnection,
          })),
        }
        if(FEcheckconstraintMessage(res,isDraft,false)) {
          return dispatch(message.warning(FEcheckconstraintMessage(res,isDraft,false)))
        }
        // NB (SPEC-021 B2): drafts do NOT carry the QC-spec keys — the analysis-draft schema
        // rejects them (422). Specs persist when the draft is saved as a definition.
        if (!id) {
          dispatch(analysisDefinitions.createDraft(res))
            .then((created) => persistForskriftLink(created && created.id))
            .then(() => navigate("/setup/analyses"))
        } else {
          // Ugly quick fix to prevent duplication
          return dispatch(analysisDefinitions.editDraft(res, id))
            .then((edited) => persistForskriftLink((edited && edited.id) || id))
        }
      }}
      saveAsName={t("saveAsNewAnalysis")}
      submitButtonName={t("save")}
      cancelButtonName={t("cancel")}
      deleteIsImportant
      isDraft={analysisDefinition ? analysisDefinition.isDraft : true}
      deleteHandler={
        id ? () => {
            shouldCheckChangesRef.current = false
            setShouldCheckChanges(false)
            dispatch(analysisDefinitions.remove(id)).then(() => navigate("/setup/analyses"))
          }
            : null
        }
      >
        {leavePageObject.display && (
          <ConfirmDialog
            onConfirm={() => {
              if (blocker.state === "blocked") {
                blocker.proceed();
              } else {
                navigate(leavePageObject.location);
              }
            }}
            onCancel={() => {
              if (blocker.state === "blocked") {
                blocker.reset();
                setLeavePageObject({ ...leavePageObject, display: false });
              } else {
                setLeavePageObject({ ...leavePageObject, display: false });
              }
            }}
          >
            <b>{t("areYouSureWantToExit")}</b>
            <div className="configurationPage-popup-list-of-changes">
              <p>{t("unsavedChangesIn")}</p>
              <ul>
                {leavePageObject.msg.split("-").slice(1).map((message, index) => (
                  <li key={index}>{message.trim()}</li>
                ))}
              </ul>
            </div>
          </ConfirmDialog>
        )}
      <div className="AnalysisConfigurationPage-metadata-container">
        <Container>
          <Title className="AnalysisName">
            {t("analysisName")}<div className="red-text">*</div>
          </Title>
          <Text required value={name} onChange={setName} />
        </Container>
        <InputGroup className="Analysis-color">
          <Title>
            {t("color")}<div className="red-text">*</div>
          </Title>
          <ColorPicker required value={color} onChange={setColor} />
        </InputGroup>
        <div className="ConfigPage-group-sample-container">
          <div className="ConfigPage-analysis-group">
          <Title>
            {t("analysisGroup")}
          </Title>
          <Dropdown
            className="AnalysisGroup-selector"
            onSelect={(eventKey) => {
              if (eventKey === "add"){
                setAddConfig(true)
              } else if (eventKey === "noGroup"){
                dispatch(analysisGroups.changeActiveAnalysisGroupId(null))
              } else {
                dispatch(analysisGroups.changeActiveAnalysisGroupId(eventKey))
              }
            }}
          >
            <Dropdown.Toggle variant="primary">{activeAnalysisGroupId ? analysisGroupsDropdown[activeAnalysisGroupId].name : t("noGroup")}</Dropdown.Toggle>
            <Dropdown.Menu>
              {Object.values(analysisGroupsDropdown).map((analysisGroup) => (
                <Dropdown.Item key={analysisGroup.id} eventKey={analysisGroup.id}>
                  {analysisGroup.name}<FiSettings className="AnalysisGroup-setting" onClick={() => {
                    setAddConfig(true)
                    setAnalysisGroupConfigId(analysisGroup.id)
                  }} />
                </Dropdown.Item>
              ))}
              <Dropdown.Divider />
              <Dropdown.Item key="add" eventKey="add">
                <FiPlus /> {t("addAnalysisGroup")}
              </Dropdown.Item>
              <Dropdown.Item key="noGroup" eventKey="noGroup">
                <FiCircle /> {t("noGroup")}
              </Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>
          </div>
          <div className="ConfigPage-sample-count">
            <Title>
              {t("maxSampleCount")}<div className="red-text">*</div>
            </Title>
            <Number
              style={{
                textAlign: "center",
                width: maxSampleCount !== null ? `calc(30px + ${maxSampleCount.toString().length * 9}px)` : "30px",
              }}
              required={!isDraft}
              min={1}
              value={maxSampleCount}
              onChange={setMaxSampleCount}
            />
            <Title>{t("minSampleCount")}</Title>
            <Number
              style={{
                textAlign: "center",
                width: minSampleCount !== null ? `calc(30px + ${minSampleCount.toString().length * 9}px)` : "30px",
              }}
              min={0}
              value={minSampleCount}
              onChange={setMinSampleCount}
            />
          </div>
        </div>
      </div>
      <p className="Pheading">{t("analysisDesignTool")}</p>
      <p className="Pheading ConfigurationPage">{t("taskDesignTool")}</p>
      <div className="AnalysisConfigurationPage-drawing">
        <GraphDesignTool
          required
          nodes={tasks}
          edges={connections}
          nodeColor={color ? color.toUpperCase() : "#FFFFFF"}
          onClear={() => {
            setTasks({})
            setConnections({})
          }}
          onCreateNode={(task) => {
            setTasks({ ...tasks, [task.id]: task })
          }}
          onEditNode={(task) => setActiveTask(task)}
          onDeleteNode={(id) => {
            const [newTasks, newConnections] = deleteNode(tasks, connections, id)
            setTasks(newTasks)
            setConnections(newConnections)
            setPerformerConstraints(
              performerConstraints
                .filter((constraint) => constraint.firstDefinitionId !== id)
                .filter((constraint) => constraint.secondDefinitionId !== id)
            )
          }}
          onCreateEdge={(prevId, nextId) => {
            const conn = {
              prevId,
              nextId,
              id: `${prevId}-${nextId}`,
              timeBetween: {
                min: 0,
                max: null,
              },
              samplePercentage: 100,
            }
            setConnections({ ...connections, [conn.id]: conn })
          }}
          onEditEdge={(connection) => setActiveConnection(connection)}
          configuredNodes={Object.values(tasks)
            // When editing a task you can't press "Save" before the name is filled - so this is valid!
            .filter((task) => task.name && task.allowedGroupIds?.length)
            .map((task) => task.id)}
          onModeChange={() => setMode(mode === "node" ? "edge" : "node")}
          mode={mode}
          onChangeInNodeCoords={setNodeCoords}
          isDraft={isDraft}
        />
        <div className="AnalysisConfigurationPage-time-container">
          <p>{t("minimumDuration", { time: formatTime(minTime) })}</p>
        </div>
      </div>
      {showLims && (
        <>
          {/* PRD-006 K19/K20: the section heading now lives INSIDE the box. The external label is
              shown only while the box is COLLAPSED, so the section is still named when closed
              without duplicating the in-box heading when open. */}
          {!showReviewSpec && <p className="Pheading ConfigurationPage">{t("reviewSpecification")}</p>}
          <div className="ConfigurationPage-performerConstraints-button">
            <Button variant="link" size="lg" onClick={() => setShowReviewSpec(!showReviewSpec)}>
              {showReviewSpec ? <FaArrowUp className="fa-arrowUp-icon" /> : <FiPlus className="fi-plus-icon" />}
            </Button>
          </div>
          {showReviewSpec && (
            <ReviewSpecification
              competenceGroups={competenceGroups}
              allowedGroupIds={reviewSpecGroups}
              onAllowedGroupIdsChange={setReviewSpecGroups}
              duration={reviewSpecDuration}
              onDurationChange={setReviewSpecDuration}
              requiresSupervisorReview={requiresSupervisorReview}
              onRequiresSupervisorReviewChange={setRequiresSupervisorReview}
            />
          )}
          {!showSstSpec && <p className="Pheading ConfigurationPage">{t("limsSstSection")}</p>}
          <div className="ConfigurationPage-performerConstraints-button">
            <Button variant="link" size="lg" onClick={() => setShowSstSpec(!showSstSpec)}>
              {showSstSpec ? <FaArrowUp className="fa-arrowUp-icon" /> : <FiPlus className="fi-plus-icon" />}
            </Button>
          </div>
          {showSstSpec && <SystemSuitability spec={sstSpec} onChange={setSstSpec} />}
        </>
      )}
      <p className="Pheading ConfigurationPage">{t("taskPerformerConstraints")}</p>
      <div className="ConfigurationPage-performerConstraints-button">
        <Button variant="link" size="lg" onClick={() => setShowPerformerConstraints(!showPerformerConstraints)}>
            {showPerformerConstraints ? <FaArrowUp className="fa-arrowUp-icon"/> : <FiPlus className="fi-plus-icon"/>}
        </Button>
      </div>
      {showPerformerConstraints && (
          <>
            <PerformerConstraints
              tasks={tasks}
              performerConstraints={performerConstraints}
              onConstraintsChange={setPerformerConstraints}
            />
          </>
        )}
        {showLims && (
          <>
            <hr />
            <p className="Pheading ConfigurationPage">{t("limsSpecification")}</p>
            <div className="ConfigurationPage-performerConstraints-button">
              <Button variant="link" size="lg" onClick={() => setShowSpecification(!showSpecification)}>
                {showSpecification ? <FaArrowUp className="fa-arrowUp-icon" /> : <FiPlus className="fi-plus-icon" />}
              </Button>
            </div>
            {showSpecification && (
              <>
                <SpecEditor
                  parameters={specParameters}
                  onParametersChange={setSpecParameters}
                  onValidityChange={setSpecValid}
                />
                {/* SPEC-021 (SPEC-007 D8): the reason for changing an existing QC spec is audited
                    server-side. It used to be an inline field HERE, under the parameter editor —
                    which meant editing the SST or the review spec popped a reason box under the
                    parameter specification (#4073/#4074). It is now asked for in a popup at save
                    time, next to the action that actually needs it. */}
                {/* F7 (SPEC-039): the QC spec is under reviewer→supervisor change control. Saving a
                    CHANGED spec creates a new draft version (the effective spec result-entry reads
                    is untouched until approved); this lists the version history + approval actions.
                    Only meaningful for an already-saved definition. */}
                {id && <SpecChangeControl analysisDefinitionId={id} />}
              </>
            )}
            <hr />
            {/* F8 (SPEC-040): high-level consumable requirements for this analysis. Folded onto the
                same nested `spec` payload as parameters/formula; drives the run-time picker filter +
                send-to-review completeness gate. */}
            <p className="Pheading ConfigurationPage">{t("limsRequiredConsumables")}</p>
            <div className="ConfigurationPage-performerConstraints-button">
              <Button variant="link" size="lg" onClick={() => setShowRequirements(!showRequirements)}>
                {showRequirements ? <FaArrowUp className="fa-arrowUp-icon" /> : <FiPlus className="fi-plus-icon" />}
              </Button>
            </div>
            {showRequirements && (
              <RequirementsSection requirements={requiredConsumables} onChange={setRequiredConsumables} />
            )}
            <hr />
            <p className="Pheading ConfigurationPage">{t("limsForskrifter")}</p>
            <div className="ConfigurationPage-performerConstraints-button">
              <Button variant="link" size="lg" onClick={() => setShowForskriftLink(!showForskriftLink)}>
                {showForskriftLink ? <FaArrowUp className="fa-arrowUp-icon" /> : <FiPlus className="fi-plus-icon" />}
              </Button>
            </div>
            {showForskriftLink && (
              <ForskriftLink
                documents={forskriftDocuments}
                selectedDocIds={forskriftDocIds}
                onChange={setForskriftDocIds}
              />
            )}
          </>
        )}
        <hr />
    </ConfigContainer>
    {/* #4073/#4074: the spec-change reason, asked at save time. Mandatory — the save is audited
        against it server-side, so there is no "save anyway". */}
    {specReasonPrompt && (
        <Popup nonScrollable onCancel={() => setSpecReasonPrompt(false)}>
          <ConfigContainer
            title={t("limsSpecChangeReason")}
            cancelHandler={() => setSpecReasonPrompt(false)}
            submitHandler={() => {
              if (!specChangeReason.trim()) {
                dispatch(message.warning(t("limsSpecChangeReasonRequired")))
                return
              }
              setSpecReasonPrompt(false)
              saveDefinition(specChangeReason)
            }}
          >
            <Container>
              <Title>{t("limsSpecChangeReasonHelp")}</Title>
              <Text value={specChangeReason} onChange={setSpecChangeReason} />
            </Container>
          </ConfigContainer>
        </Popup>
      )}
    {activeTask && (
        <Popup onCancel={() => setActiveTask(null)}>
          <AnalysisTaskConfig
            title={t("editTask")}
            isDefinition
            task={activeTask}
            submitHandler={(res) => {
              setTasks(addToMap(tasks, { ...activeTask, ...res }))
              setActiveTask(null)
            }}
            isDraft={isDraft}
            deleteHandler={null}
            cancelHandler={() => setActiveTask(null)}
          />
        </Popup>
      )}
      {addConfig && (
        <Popup nonScrollable onCancel={() => setAddConfig(false)}>
          <AnalysisGroupConfig
            analysisGroup={analysisGroupsDropdown[analysisGroupConfigId]}
            onClose={() => {
              setAddConfig(false)
              setAnalysisGroupConfigId(null)
            }}
            onSubmit={() => {
              setAddConfig(false)
              setAnalysisGroupConfigId(null)
            }}
          />
        </Popup>
      )}
      {activeConnection && (
        <Popup nonScrollable onCancel={() => setActiveConnection(null)}>
          <AnalysisConnectionConfig
            connection={activeConnection}
            taskFrom={tasks[activeConnection.prevId]}
            taskTo={tasks[activeConnection.nextId]}
            submitHandler={(res) => {
              setConnections(addToMap(connections, { ...activeConnection, ...res }))
              setActiveConnection(null)
            }}
            deleteHandler={() => {
              setConnections(removeFromMap(connections, activeConnection))
              setActiveConnection(null)
            }}
            cancelHandler={() => setActiveConnection(null)}
          />
        </Popup>
      )}
    </>
  )
}

